import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/AuthContext';
import './SettingsView.css';

type ThemeMode = 'light' | 'dark' | 'system';

interface SettingsViewProps {
    themeMode: ThemeMode;
    onThemeChange: (mode: ThemeMode) => void;
}

const SettingsView: React.FC<SettingsViewProps> = ({
    themeMode,
    onThemeChange
}) => {
    const { t, i18n } = useTranslation();
    const { user, hasRole, startImpersonation, stopImpersonation, isImpersonating, impersonatedRole } = useAuth();

    const languages = [
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'it', name: 'Italiano', flag: '🇮🇹' }
    ];

    const handleLanguageChange = (languageCode: string) => {
        i18n.changeLanguage(languageCode);
    };

    const handleThemeChange = (mode: ThemeMode) => {
        onThemeChange(mode);
    };

    // Test roles for admin impersonation
    const testRoles = [
        { role: 'ADMIN', label: 'Admin User', icon: 'admin_panel_settings', color: '#dc3545' },
        { role: 'USER', label: 'Pro User', icon: 'star', color: '#007bff' },
        { role: 'FREE', label: 'Free User', icon: 'person', color: '#ffc107' },
        { role: 'GUEST', label: 'Guest User', icon: 'visibility', color: '#28a745' }
    ];

    return (
        <div className="settings-container">
            <div className="settings-header">
                <h1>{t('settings.title')}</h1>
                <p>{t('settings.description')}</p>
            </div>

            <div className="settings-content">
                {/* Theme Settings Section */}
                <div className="settings-section">
                    <div className="section-header">
                        <span className="material-icons">palette</span>
                        <h2>{t('settings.theme.title')}</h2>
                    </div>
                    <p className="section-description">{t('settings.theme.description')}</p>
                    
                    <div className="theme-options">
                        <div 
                            className={`theme-card ${themeMode === 'light' ? 'active' : ''}`}
                            onClick={() => handleThemeChange('light')}
                        >
                            <div className="theme-preview light-preview">
                                <div className="preview-header"></div>
                                <div className="preview-content">
                                    <div className="preview-text"></div>
                                    <div className="preview-text short"></div>
                                </div>
                            </div>
                            <div className="theme-info">
                                <span className="material-icons">light_mode</span>
                                <div>
                                    <h3>{t('profile.lightMode')}</h3>
                                    <p>{t('settings.theme.lightDescription')}</p>
                                </div>
                            </div>
                        </div>

                        <div 
                            className={`theme-card ${themeMode === 'dark' ? 'active' : ''}`}
                            onClick={() => handleThemeChange('dark')}
                        >
                            <div className="theme-preview dark-preview">
                                <div className="preview-header"></div>
                                <div className="preview-content">
                                    <div className="preview-text"></div>
                                    <div className="preview-text short"></div>
                                </div>
                            </div>
                            <div className="theme-info">
                                <span className="material-icons">dark_mode</span>
                                <div>
                                    <h3>{t('profile.darkMode')}</h3>
                                    <p>{t('settings.theme.darkDescription')}</p>
                                </div>
                            </div>
                        </div>

                        <div 
                            className={`theme-card ${themeMode === 'system' ? 'active' : ''}`}
                            onClick={() => handleThemeChange('system')}
                        >
                            <div className="theme-preview system-preview">
                                <div className="preview-header"></div>
                                <div className="preview-content">
                                    <div className="preview-text"></div>
                                    <div className="preview-text short"></div>
                                </div>
                            </div>
                            <div className="theme-info">
                                <span className="material-icons">brightness_auto</span>
                                <div>
                                    <h3>{t('profile.systemMode')}</h3>
                                    <p>{t('settings.theme.systemDescription')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Language Settings Section */}
                <div className="settings-section">
                    <div className="section-header">
                        <span className="material-icons">language</span>
                        <h2>{t('settings.language.title')}</h2>
                    </div>
                    <p className="section-description">{t('settings.language.description')}</p>
                    
                    <div className="language-grid">
                        {languages.map((language) => (
                            <button
                                key={language.code}
                                className={`language-card ${i18n.language === language.code ? 'active' : ''}`}
                                onClick={() => handleLanguageChange(language.code)}
                            >
                                <span className="language-flag">{language.flag}</span>
                                <span className="language-name">{language.name}</span>
                                {i18n.language === language.code && (
                                    <span className="material-icons check-icon">check_circle</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Admin Impersonation Section */}
                {hasRole('ADMIN') && (
                    <div className="settings-section admin-section">
                        <div className="section-header">
                            <span className="material-icons">theater_comedy</span>
                            <h2>{t('settings.impersonation.title')}</h2>
                        </div>
                        <p className="section-description">{t('settings.impersonation.description')}</p>
                        
                        <div className="impersonation-warning">
                            <span className="material-icons">warning</span>
                            <div>
                                <strong>{t('settings.impersonation.warningTitle')}</strong>
                                <p>{t('settings.impersonation.warningText')}</p>
                            </div>
                        </div>

                        {isImpersonating && (
                            <div className="impersonation-active">
                                <span className="material-icons">theater_comedy</span>
                                <div>
                                    <strong>{t('settings.impersonation.activeTitle')}</strong>
                                    <p>{t('settings.impersonation.activeText', { role: impersonatedRole })}</p>
                                    <button 
                                        className="stop-impersonation-button"
                                        onClick={stopImpersonation}
                                    >
                                        <span className="material-icons">stop</span>
                                        {t('settings.impersonation.stopTesting')}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="role-test-grid">
                            {testRoles.map((roleInfo) => (
                                <div 
                                    key={roleInfo.role}
                                    className="role-card"
                                    style={{ '--role-color': roleInfo.color } as React.CSSProperties}
                                >
                                    <div className="role-header">
                                        <span 
                                            className="material-icons role-icon"
                                            style={{ color: roleInfo.color }}
                                        >
                                            {roleInfo.icon}
                                        </span>
                                        <h3>{roleInfo.label}</h3>
                                    </div>
                                    <div className="role-actions">
                                        <button 
                                            className="test-button"
                                            onClick={() => {
                                                if (impersonatedRole === roleInfo.role) {
                                                    stopImpersonation();
                                                } else {
                                                    startImpersonation(roleInfo.role as 'ADMIN' | 'USER' | 'FREE' | 'GUEST');
                                                }
                                            }}
                                        >
                                            <span className="material-icons">
                                                {impersonatedRole === roleInfo.role ? 'stop' : 'play_arrow'}
                                            </span>
                                            {impersonatedRole === roleInfo.role ? 
                                                t('settings.impersonation.stopTesting') : 
                                                `${t('settings.impersonation.testAs')} ${roleInfo.label}`
                                            }
                                        </button>
                                    </div>
                                    <div className="role-permissions">
                                        <p>{t(`settings.impersonation.${roleInfo.role.toLowerCase()}Permissions`)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Account Information Section */}
                <div className="settings-section">
                    <div className="section-header">
                        <span className="material-icons">account_circle</span>
                        <h2>{t('settings.account.title')}</h2>
                    </div>
                    <p className="section-description">{t('settings.account.description')}</p>
                    
                    <div className="account-info">
                        <div className="account-card">
                            <div className="card-header">
                                <span className="material-icons">person</span>
                                <h3>{t('settings.account.personalInfo')}</h3>
                            </div>
                            <div className="info-grid">
                                <div className="info-row">
                                    <span className="info-label">{t('settings.account.username')}</span>
                                    <span className="info-value">{user?.username}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">{t('settings.account.email')}</span>
                                    <div className="info-value-with-status">
                                        <span className="info-value">{user?.email}</span>
                                        <span className={`verification-status ${user?.emailVerified ? 'verified' : 'unverified'}`}>
                                            <span className="material-icons">
                                                {user?.emailVerified ? 'verified' : 'warning'}
                                            </span>
                                            {user?.emailVerified ? t('settings.account.verified') : t('settings.account.unverified')}
                                        </span>
                                    </div>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">{t('settings.account.userId')}</span>
                                    <span className="info-value user-id">{user?.id}</span>
                                </div>
                            </div>
                        </div>

                        <div className="account-card">
                            <div className="card-header">
                                <span className="material-icons">security</span>
                                <h3>{t('settings.account.roleAndPermissions')}</h3>
                            </div>
                            <div className="info-grid">
                                <div className="info-row">
                                    <span className="info-label">{t('settings.account.role')}</span>
                                    <span className={`role-badge ${user?.role?.toLowerCase()}`}>
                                        <span className="material-icons">
                                            {user?.role === 'ADMIN' ? 'admin_panel_settings' : 
                                             user?.role === 'USER' ? 'star' : 
                                             user?.role === 'FREE' ? 'person' : 'help'}
                                        </span>
                                        {user?.role}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">{t('settings.account.permissions')}</span>
                                    <span className="info-value">
                                        {user?.role === 'ADMIN' ? t('settings.account.adminPermissions') :
                                         user?.role === 'USER' ? t('settings.account.proPermissions') :
                                         user?.role === 'FREE' ? t('settings.account.freePermissions') :
                                         t('settings.account.guestPermissions')}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="account-card">
                            <div className="card-header">
                                <span className="material-icons">schedule</span>
                                <h3>{t('settings.account.activityInfo')}</h3>
                            </div>
                            <div className="info-grid">
                                <div className="info-row">
                                    <span className="info-label">{t('settings.account.memberSince')}</span>
                                    <span className="info-value">
                                        {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : t('settings.account.notAvailable')}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">{t('settings.account.lastLogin')}</span>
                                    <span className="info-value">
                                        {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : t('settings.account.notAvailable')}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">{t('settings.account.profileUpdated')}</span>
                                    <span className="info-value">
                                        {user?.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : t('settings.account.notAvailable')}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Subscription Info for non-admin users */}
                        {user?.role !== 'ADMIN' && (
                            <div className="account-card">
                                <div className="card-header">
                                    <span className="material-icons">
                                        {user?.role === 'USER' ? 'star' : 'account_circle'}
                                    </span>
                                    <h3>{t('settings.account.subscriptionInfo')}</h3>
                                </div>
                                <div className="info-grid">
                                    <div className="info-row">
                                        <span className="info-label">{t('settings.account.planType')}</span>
                                        <span className={`plan-badge ${user?.role?.toLowerCase()}`}>
                                            {user?.role === 'USER' ? t('settings.account.proPlan') : t('settings.account.freePlan')}
                                        </span>
                                    </div>
                                    {user?.role === 'USER' && (
                                        <>
                                            <div className="info-row">
                                                <span className="info-label">{t('settings.account.subscriptionStatus')}</span>
                                                <span className="info-value status-active">
                                                    <span className="material-icons">check_circle</span>
                                                    {t('settings.account.active')}
                                                </span>
                                            </div>
                                            <div className="info-row">
                                                <span className="info-label">{t('settings.account.billingCycle')}</span>
                                                <span className="info-value">{t('settings.account.monthly')}</span>
                                            </div>
                                        </>
                                    )}
                                    {user?.role === 'FREE' && (
                                        <div className="info-row">
                                            <span className="info-label">{t('settings.account.limitations')}</span>
                                            <span className="info-value">{t('settings.account.freeLimitations')}</span>
                                        </div>
                                    )}
                                </div>
                                
                                {user?.role === 'FREE' && (
                                    <div className="upgrade-section">
                                        <button className="upgrade-button">
                                            <span className="material-icons">star</span>
                                            {t('settings.account.upgradeToPro')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Security Settings */}
                        <div className="account-card">
                            <div className="card-header">
                                <span className="material-icons">shield</span>
                                <h3>{t('settings.account.security')}</h3>
                            </div>
                            <div className="security-actions">
                                <button className="security-button">
                                    <span className="material-icons">lock</span>
                                    <div>
                                        <strong>{t('settings.account.changePassword')}</strong>
                                        <p>{t('settings.account.changePasswordDesc')}</p>
                                    </div>
                                    <span className="material-icons">chevron_right</span>
                                </button>
                                
                                {!user?.emailVerified && (
                                    <button className="security-button">
                                        <span className="material-icons">mark_email_unread</span>
                                        <div>
                                            <strong>{t('settings.account.verifyEmail')}</strong>
                                            <p>{t('settings.account.verifyEmailDesc')}</p>
                                        </div>
                                        <span className="material-icons">chevron_right</span>
                                    </button>
                                )}

                                <button className="security-button danger">
                                    <span className="material-icons">delete_forever</span>
                                    <div>
                                        <strong>{t('settings.account.deleteAccount')}</strong>
                                        <p>{t('settings.account.deleteAccountDesc')}</p>
                                    </div>
                                    <span className="material-icons">chevron_right</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsView; 